import { markAsDraft } from '@/api/order-api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { formatDateLabel, formatTimeLabel } from '@/lib/utils';
import type { OrderResponse, OrderStateHistory } from '@/types/order-type';
import { Typography } from '@mui/material';
import { Clock, Ellipsis, FileText } from 'lucide-react';
import { useMemo } from 'react';
import Iconify from '../iconify/iconify';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import TimelineEntry from './timeline/TimelineEntry';
import type { TimelineItem } from './timeline/TimelineEntry';
import TimelineProgressChips from './timeline/TimelineProgressChips';
import { STATUS_META, humanizeState } from './timeline/timeline-constants';

interface OrderStatusTimelineProps {
  order: OrderResponse;
  history: OrderStateHistory[];
  onRefresh?: () => void;
}

export default function OrderStatusTimeline({
  order,
  history,
  onRefresh,
}: OrderStatusTimelineProps) {
  const sortedHistory = useMemo(
    () =>
      [...history].sort(
        (a, b) => new Date(a.createTime).getTime() - new Date(b.createTime).getTime(),
      ),
    [history],
  );

  const currentState =
    sortedHistory.length > 0
      ? sortedHistory[sortedHistory.length - 1].currentState ||
        sortedHistory[sortedHistory.length - 1].orderState
      : order.status;

  const timelineItems: TimelineItem[] = useMemo(() => {
    if (!sortedHistory.length) {
      const createdAt = order.createdAt;
      return [
        {
          id: String(order.id),
          state: 'DRAFT',
          title: 'Order Created',
          desc: 'Your order has been created.',
          date: createdAt ? formatDateLabel(createdAt) : null,
          time: createdAt ? formatTimeLabel(createdAt) : null,
          note: null,
          actor: null,
          icon: <FileText className="h-3.5 w-3.5" />,
          isCurrent: true,
        },
      ];
    }

    return sortedHistory.map((h, idx) => {
      const state = h.currentState || h.orderState;
      const meta = STATUS_META[state] ?? {
        title: humanizeState(state),
        desc: 'Order status was updated.',
        icon: <Clock className="h-3.5 w-3.5" />,
      };

      return {
        id: String(h.id),
        state,
        title: meta.title,
        desc: h.note || meta.desc,
        date: h.createTime ? formatDateLabel(h.createTime) : null,
        time: h.createTime ? formatTimeLabel(h.createTime) : null,
        note: h.note,
        actor: h.actionByUsername,
        icon: meta.icon,
        isCurrent: idx === sortedHistory.length - 1,
      };
    });
  }, [sortedHistory, order]);

  const seenStates = new Set(sortedHistory.map((h) => h.currentState || h.orderState));

  const handleMarkAsDraft = async () => {
    const resp = await markAsDraft(order.id);
    if (resp.success) {
      onRefresh?.();
    } else {
      toast({
        title: 'Could not mark order as draft',
        description: resp.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card className="border border-slate-200 shadow-sm rounded-xl">
      <CardHeader className="py-3 px-3 sm:px-4 flex justify-between border-b border-slate-100">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            color: 'text.primary',
          }}
        >
          Order Status
        </Typography>

        {currentState === 'DRAFT' && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="More actions">
                <Ellipsis className="h-4 w-4 text-slate-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={handleMarkAsDraft} className="gap-2 cursor-pointer">
                <Iconify icon="mdi:pencil-outline" className="h-4 w-4 text-slate-500" />
                Move back to Draft
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </CardHeader>

      <CardContent className="px-3 sm:px-4 py-4 max-h-[420px] lg:max-h-[800px] overflow-y-auto">
        <ol className="relative">
          {timelineItems.map((item, idx) => (
            <TimelineEntry
              key={item.id}
              item={item}
              isLast={idx === timelineItems.length - 1}
            />
          ))}
        </ol>

        <TimelineProgressChips currentState={currentState} seenStates={seenStates} />
      </CardContent>
    </Card>
  );
}
